import {
  forwardRef,
  type CSSProperties,
  type InputHTMLAttributes,
  useId,
  useState,
} from "react";
import { tokens } from "../../design-system/tokens";
import styles from "./Input.module.css";

type InputSize = "small" | "medium" | "large";
type InputColor = "secondary" | "outline" | "outlineLight" | "outlineSecondary";

function toCssLength(value: number | string | undefined): string | undefined {
  if (value === undefined || value === "") return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
}

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "color"> {
  /** Helper text shown above the input. */
  label?: string;
  /** Applied from 768px up. Omitted on smaller viewports unless `mobileWidth` is set. */
  width?: string | number;
  /** Caps width (e.g. narrow key fields). Applied as inline `max-width` so it wins over module defaults. */
  maxWidth?: string | number;
  /** Overrides default full-width on viewports below 768px (default is `100%`). */
  mobileWidth?: string | number;
  size?: InputSize;
  color?: InputColor;
  rtl?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    disabled = false,
    label,
    width,
    maxWidth,
    mobileWidth,
    size = "large",
    color = "secondary",
    rtl = false,
    id: idProp,
    ...rest
  },
  ref,
) {
  const [isFocused, setIsFocused] = useState(false);
  const generatedId = useId();
  const inputId = idProp ?? generatedId;

  const sizeConfig = {
    small: {
      height: "30px",
      padding: "0px 10px",
      borderRadius: tokens.borderRadius.md,
      typography: tokens.typography.inputSmall,
      labelTypography: tokens.typography.caption,
    },
    medium: {
      height: "36px",
      padding: "0px 11px",
      borderRadius: tokens.borderRadius.lg,
      typography: tokens.typography.inputMedium,
      labelTypography: tokens.typography.caption,
    },
    large: {
      height: "40px",
      padding: "0px 12px",
      borderRadius: tokens.borderRadius.xl,
      typography: tokens.typography.inputLarge,
      labelTypography: tokens.typography.caption,
    },
  } as const;
  const currentSize = sizeConfig[size];

  const isOutline = color === "outline";
  const isOutlineLight = color === "outlineLight";
  const isOutlineSecondary = color === "outlineSecondary";
  /** Outline variants keep a stable background on focus (border-only focus ring). */
  const baseBackgroundColor = isOutline || isOutlineSecondary
    ? "transparent"
    : isOutlineLight
      ? "transparent"
      : tokens.buttonColors.secondaryLight;
  const borderColor = isOutline
    ? tokens.colors.inputBorder
    : isOutlineSecondary
      ? isFocused
        ? tokens.buttonColors.secondary
        : tokens.buttonColors.outlineSecondary
      : isFocused
        ? tokens.colors.text.inverse
        : isOutlineLight
          ? tokens.buttonColors.secondaryLight
          : tokens.buttonColors.secondaryLight;
  const textColor = isOutlineLight
    ? tokens.colors.text.inverse
    : isOutline || isOutlineSecondary
      ? tokens.colors.text.primary
      : tokens.colors.primary;
  const placeholderColor = isOutlineLight
    ? tokens.colors.text.inverse
    : isOutline || isOutlineSecondary
      ? tokens.colors.text.primaryPlaceholder
      : tokens.colors.text.tertiary;

  const desktopWidth = toCssLength(width);
  const maxWidthCss = toCssLength(maxWidth);
  const mobileW =
    mobileWidth != null && mobileWidth !== ""
      ? (toCssLength(mobileWidth) ?? "100%")
      : "100%";

  const widthStyle = {
    "--ds-input-width-mobile": mobileW,
    ...(desktopWidth !== undefined ? { "--ds-input-width-desktop": desktopWidth } : {}),
    ...(maxWidthCss ? { maxWidth: maxWidthCss } : {}),
  } as CSSProperties;

  const mergedClassName = [
    styles.input,
    desktopWidth !== undefined ? styles.inputHasDesktopWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inputElement = (
    <input
      ref={ref}
      id={label ? inputId : idProp}
      {...rest}
      disabled={disabled}
      className={mergedClassName}
      onChange={(event) => {
        rest.onChange?.(event);
      }}
      onFocus={(event) => {
        setIsFocused(true);
        rest.onFocus?.(event);
      }}
      onBlur={(event) => {
        setIsFocused(false);
        rest.onBlur?.(event);
      }}
      onKeyDown={(event) => {
        rest.onKeyDown?.(event);
      }}
      style={{
        "--input-placeholder-color": placeholderColor,
        "--ds-input-font-family": currentSize.typography.fontFamily,
        "--ds-input-font-size": currentSize.typography.fontSize,
        ...(!label ? widthStyle : {}),
        height: currentSize.height,
        padding: currentSize.padding,
        borderRadius: currentSize.borderRadius,
        border: `1px solid ${borderColor}`,
        background: baseBackgroundColor,
        color: textColor,
        fontWeight: currentSize.typography.fontWeight,
        lineHeight: String(currentSize.typography.lineHeight),
        textAlign: rtl ? "right" : "center",
        direction: rtl ? "rtl" : "ltr",
        transition: `background-color ${tokens.transitions.normal}, border-color ${tokens.transitions.normal}`,
        outline: "none",
        boxSizing: "border-box",
        ...(label ? {} : maxWidthCss ? { maxWidth: maxWidthCss } : {}),
      } as CSSProperties}
    />
  );

  if (!label) {
    return inputElement;
  }

  const labelTypography = currentSize.labelTypography;

  return (
    <div
      className={`${styles.wrap} ${rtl ? styles.wrapRtl : styles.wrapLtr}`}
      style={widthStyle}
    >
      <label
        htmlFor={inputId}
        className={styles.helperLabel}
        style={{
          fontFamily: labelTypography.fontFamily,
          fontSize: labelTypography.fontSize,
          fontWeight: labelTypography.fontWeight,
          lineHeight: String(labelTypography.lineHeight),
          color: tokens.colors.text.secondary,
          direction: rtl ? "rtl" : "ltr",
        }}
      >
        {label}
      </label>
      {inputElement}
    </div>
  );
});

Input.displayName = "Input";

export default Input;
